import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Download, Shield, Clock, MessageSquareReply, FileCheck2, FileSpreadsheet,
  BookText, Zap, Siren, Lock, ArrowRight, AlertTriangle, HeartHandshake, CalendarCheck,
} from "lucide-react";
import { useCampusAuth } from "@/contexts/CampusAuth";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/campus/vault`;

const TIER_RANK = {
  "Helden-Basic": 0,
  "Kurs-Bonus": 1,
  "Single-Held": 2,
  "Hauptquartier": 3,
  "Admin": 99,
};

const ITEMS = [
  { slug: "mdk-notfall-poster", icon: Siren, title: "MDK-Notfall-Poster A4",
    subtitle: "Zum Aushängen im PDL-Büro",
    desc: "Die 3 Säulen des MDK-Schutzschilds auf einer Seite: Roter Knopf mit QR-Code zum Campus, 3-Min-Spickzettel und die Konter-Matrix.",
    type: "PDF · 1 Seite · A4", highlight: true, minTier: "Helden-Basic" },
  { slug: "mdk-soforthilfe-protokoll", icon: Shield, title: "MDK-Soforthilfe-Protokoll",
    subtitle: "Die ersten 30 Minuten der Prüfung",
    desc: "Glasklare Checkliste für den Tag, an dem der Medizinische Dienst unangemeldet vor der Tür steht.",
    type: "PDF · 1 Seite", minTier: "Helden-Basic" },
  { slug: "angehoerigen-beschwerde-1pager", icon: Zap, title: "Angehörigen-Beschwerde · 1-Pager",
    subtitle: "Sofort ausdrucken & neben den PC hängen",
    desc: "Fall A (mangelnde Pflege) als 1-seitiger Spickzettel mit dem 4-Schritte-Prinzip und fertiger Antwort-Mail.",
    type: "PDF · 1 Seite", minTier: "Kurs-Bonus" },
  { slug: "erschoepfungs-dienstplan", icon: Clock, title: "Der Erschöpfungs-Dienstplan",
    subtitle: "Notfallkonzept bei akutem Personalausfall",
    desc: "Zwei Notfallstufen, ArbZG-konforme Maßnahmen-Kaskade, fertige MAV-Mail, Notfall-Protokoll zum Ausfüllen.",
    type: "PDF · 4 Seiten", minTier: "Kurs-Bonus" },
  { slug: "angehoerigen-beschwerde-leitfaden", icon: MessageSquareReply, title: "Angehörigen-Beschwerde · 5-Min-Leitfaden",
    subtitle: "Textbausteine für 4 brisante Fälle",
    desc: "Vier fertige Antwort-Mails plus 4-Schritte-Prinzip, Do's & Don'ts, Eskalations-Stufen.",
    type: "PDF · 5 Seiten", minTier: "Single-Held" },
  { slug: "entbuerokratisierung-sis-checkliste", icon: FileCheck2, title: "Entbürokratisierungs-Checkliste",
    subtitle: "Der SIS-Filter: MDK-sicher & schlank",
    desc: "Für jedes SIS-Themenfeld (A1–A6) plus Pflegebericht: was kann weg, was muss rein. Quick-Filter aus 4 Fragen.",
    type: "PDF · 4 Seiten", minTier: "Single-Held" },
  { slug: "pflegesatz-verhandlungs-matrix", icon: FileSpreadsheet, title: "Pflegesatz-Verhandlungs-Matrix",
    subtitle: "Excel-Vorlage mit Formeln & Auswertung",
    desc: "Spalten A–G, Validierungen, VLOOKUP-Soll-Minuten, Conditional Formatting, Auswertungs-Panel.",
    type: "XLSX · 4 Sheets", minTier: "Single-Held" },
  { slug: "pflegesatz-verhandlungs-leitfaden", icon: BookText, title: "Pflegesatz-Verhandlungs-Leitfaden",
    subtitle: "Begleit-PDF zur Matrix",
    desc: "3 unschlagbare Verhandlungs-Argumente, 4-Wochen-Vorbereitungsplan, Verhandlungs-Hygiene.",
    type: "PDF · 4 Seiten", minTier: "Single-Held" },
  { slug: "sturz-bewohner", icon: AlertTriangle, title: "Sturz eines Bewohners · Akutleitfaden",
    subtitle: "Die ersten 5 Minuten richtig handeln",
    desc: "7-Schritte-Akutkaskade, rote Flaggen für den Notruf, rechtssicheres Sturzprotokoll mit Formulierungsbeispielen, Angehörigen-Gespräch.",
    type: "PDF · 3 Seiten", minTier: "Helden-Basic" },
  { slug: "demenz-25-saetze", icon: HeartHandshake, title: "Demenz · 25 Sätze, die wirklich helfen",
    subtitle: "Validation für die 5 häufigsten Situationen",
    desc: "25 erprobte Sätze mit „Warum es wirkt“ – Unruhe, Pflegeverweigerung, Heimweh, Aggression, gute Momente. Plus 5 No-Go-Sätze.",
    type: "PDF · 3 Seiten", minTier: "Helden-Basic" },
  { slug: "md-pruefung-7-tage", icon: CalendarCheck, title: "MD-Prüfung · 7-Tage-Plan",
    subtitle: "Von Tag 7 bis zum Prüfungstag",
    desc: "Jeden Tag ein Fokus: Stichprobe, Risikobereiche, Medikamente, Hygiene, Personal. Plus Top-8-Abwertungsgründe mit Gegenmittel.",
    type: "PDF · 3 Seiten", minTier: "Helden-Basic" },
];

export default function CampusSchatzkiste() {
  const { user } = useCampusAuth();
  const userRank = TIER_RANK[user?.tier] ?? 0;
  const lockedCount = ITEMS.filter((it) => TIER_RANK[it.minTier] > userRank).length;
  const [downloadingErr, setDownloadingErr] = useState(null);

  // Onboarding-Schritt „Schatzkiste entdecken" als erledigt markieren
  useEffect(() => {
    try { localStorage.setItem("dh-visited-schatzkiste", "1"); } catch {
      // localStorage evtl. deaktiviert – ignorieren
    }
  }, []);

  const handleClick = async (item, e) => {
    if (TIER_RANK[item.minTier] > userRank) {
      e.preventDefault();
      setDownloadingErr(item.slug);
      return;
    }
    // Otherwise let the browser follow the link with cookies.
  };

  return (
    <div className="space-y-12">
      <header>
        <div className="text-[12px] uppercase tracking-[0.28em] font-medium text-sand-500 mb-3">
          Schatzkiste  ·  {ITEMS.length} Dokumente
        </div>
        <h1 className="font-display text-sand-800 text-3xl sm:text-[2.4rem] font-semibold leading-[1.1]">
          Deine digitale <span className="text-terra">Zeitmaschine.</span>
        </h1>
        <p className="mt-4 text-sand-600 text-[15.5px] leading-[1.65] max-w-2xl">
          Mustervorlagen, Checklisten und Argumentationshilfen – monatlich erweitert.
        </p>
        {lockedCount > 0 && (
          <UpgradeBanner userTier={user?.tier} lockedCount={lockedCount} />
        )}
      </header>

      <section className="grid md:grid-cols-2 gap-5 lg:gap-6">
        {ITEMS.map((item) => {
          const locked = TIER_RANK[item.minTier] > userRank;
          const href = `${API_BASE}/${item.slug}/download`;
          return (
            <article
              key={item.slug}
              id={item.slug}
              data-testid={`vault-item-${item.slug}`}
              data-locked={locked ? "true" : "false"}
              className={`group relative rounded-2xl border p-6 lg:p-7 flex flex-col transition-colors ${
                locked
                  ? "bg-cream-50 border-cream-300 opacity-95"
                  : item.highlight
                  ? "bg-peach-50 border-terra/30 hover:border-terra"
                  : "bg-white border-cream-300 hover:border-sand-400"
              }`}
            >
              <div className="flex items-start justify-between mb-5">
                <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${
                  locked ? "bg-sand-200 text-sand-500" : "bg-terra/10 text-terra"
                }`}>
                  <item.icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-sand-500">
                  {item.type}
                </span>
              </div>
              <h3 className={`font-display text-[1.15rem] font-semibold leading-tight ${locked ? "text-sand-600" : "text-sand-800"}`}>
                {item.title}
              </h3>
              <div className={`mt-1 text-[13px] leading-snug ${locked ? "text-sand-500" : "text-sand-600"}`}>{item.subtitle}</div>
              <p className={`mt-4 text-[13.5px] leading-relaxed flex-1 ${locked ? "text-sand-500" : "text-sand-600"}`}>
                {item.desc}
              </p>

              {locked ? (
                <Link
                  to={item.minTier === "Single-Held" ? "/campus/pro" : "/campus/team"}
                  data-testid={`vault-upgrade-${item.slug}`}
                  className="mt-6 inline-flex items-center justify-between gap-2 rounded-xl border border-terra/40 bg-white hover:bg-peach-50 px-4 h-11 text-[13.5px] font-medium text-terra transition-colors"
                >
                  <span className="inline-flex items-center gap-2">
                    <Lock className="h-4 w-4" strokeWidth={1.7} />
                    Upgrade auf {item.minTier} freischalten
                  </span>
                  <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                </Link>
              ) : (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => handleClick(item, e)}
                  data-testid={`vault-download-${item.slug}`}
                  className="mt-6 inline-flex items-center justify-between gap-2 rounded-xl border border-cream-300 bg-cream-50 hover:bg-cream-100 px-4 h-11 text-[13.5px] font-medium text-sand-800 transition-colors"
                >
                  <span className="inline-flex items-center gap-2">
                    <Download className="h-4 w-4" strokeWidth={1.6} />
                    Herunterladen
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-sand-500">
                    {item.type.split("·")[0].trim()}
                  </span>
                </a>
              )}
              {downloadingErr === item.slug && (
                <p className="mt-2 text-[12px] text-terra font-medium">
                  Bitte zuerst upgraden, um dieses Dokument zu öffnen.
                </p>
              )}
            </article>
          );
        })}
      </section>
    </div>
  );
}

function UpgradeBanner({ userTier, lockedCount }) {
  const nextTier = userTier === "Helden-Basic" || !userTier ? "Single-Held" : "Hauptquartier";
  const target = nextTier === "Single-Held" ? "/campus/pro" : "/campus/team";
  return (
    <div
      data-testid="vault-upgrade-banner"
      className="mt-7 inline-flex items-center gap-3 rounded-2xl border border-terra/30 bg-peach-50 px-5 py-3 text-[13.5px] text-sand-800"
    >
      <Lock className="h-4 w-4 text-terra" strokeWidth={1.8} />
      <span>
        <strong className="text-sand-900">{lockedCount} Dokumente</strong> warten auf dich.
        Mit <strong className="text-terra">{nextTier}</strong> bekommst du alles frei.
      </span>
      <Link
        to={target}
        className="ml-2 inline-flex items-center gap-1.5 text-terra font-semibold underline-offset-4 hover:underline"
      >
        Freischalten <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
      </Link>
    </div>
  );
}
